<template>
  <div class="relative flex size-full min-h-screen flex-col group/design-root overflow-x-hidden bg-slate-50 dark:bg-slate-900" style="font-family: Inter, 'Noto Sans', sans-serif;"
>
    <!-- Main Content -->
    <main class="flex-1 estimation-page-layout">
      <div class="estimation-content-wrapper">
        
        <!-- Header Section -->
        <div class="estimation-header-section">
          <div class="header-content">
            <h1 class="text-slate-900 dark:text-slate-100">
              {{ editingBudgetId ? 'Editar Estimación por Items' : 'Estimación por Items' }}
            </h1>
            <p class="text-slate-600 dark:text-slate-400">
              Crear estimaciones detalladas seleccionando servicios específicos y calculando horas por item.
            </p>
            <div v-if="editingBudgetId" class="text-sm text-slate-500 dark:text-slate-400">
              ID: #{{ editingBudgetId }}
            </div>
          </div>
          <div class="header-actions">
            <div class="action-group">
              <button 
                @click="saveBudget" 
                :disabled="!isFormValid"
                :class="[
                  'w-full sm:w-auto transition-all duration-200',
                  isFormValid 
                    ? 'btn btn-primary' 
                    : 'btn btn-primary opacity-50 cursor-not-allowed'
                ]"
              >
                <i class="material-icons">save</i>
                <span class="truncate">{{ editingBudgetId ? 'Actualizar Estimación' : 'Guardar Estimación' }}</span>
              </button>
              <p v-if="!isFormValid" class="text-sm text-orange-600 dark:text-orange-400 text-right whitespace-nowrap">
                Complete los campos obligatorios (*)
              </p>
            </div>
          </div>
        </div>

        <!-- Project Information Section -->
        <div class="estimation-project-info">
          <h2 class="text-slate-900 dark:text-slate-100">Información del Proyecto</h2>
          <base-info-form
            :initial-client-name="clientName"
            :initial-owner-name="ownerName"
            :initial-jira-ticket="jiraTicket"
            :initial-intranet-url="intranetUrl"
            @update-client-name="updateClientName"
            @update-owner-name="updateOwnerName"
            @update-jira-ticket="updateJiraTicket"
            @update-intranet-url="updateIntranetUrl"
            @validation-change="updateFormValidation"
          />
        </div>

        <!-- Main Content Grid -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          <!-- Left Column -->
          <div class="lg:col-span-1 space-y-6">
            <!-- Services Section -->
            <div class="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 space-y-4">
              <!-- Service Selector with Guide Link -->
              <div class="space-y-2">
                <div class="flex items-center justify-between">
                  <h3 class="text-slate-900 dark:text-slate-100 text-sm font-medium">Servicios Disponibles</h3>
                  <router-link 
                    to="/admin/items"
                    class="flex items-center gap-1 text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 text-sm font-medium transition-colors"
                  >
                    <span class="material-icons-outlined text-base">settings</span>
                    <span>Administrar Items</span>
                  </router-link>
                </div>
                <service-selector
                  :selected-service="selectedService"
                  @service-selected="selectService"
                  @add-item="handleAddItem"
                />
              </div>
              
              <!-- Supervision Only Checkbox -->
              <div class="flex items-center gap-2 mb-3">
                <input
                  id="supervision-only-service"
                  v-model="supervisionOnlyService"
                  type="checkbox"
                  class="form-checkbox h-4 w-4 text-orange-600 focus:ring-orange-500 border-slate-300 dark:border-slate-600 rounded"
                />
                <label for="supervision-only-service" class="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1">
                  <span class="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
                  Solo Supervisión
                  <span class="text-xs text-slate-500 dark:text-slate-400">(No incluido en subtotal)</span>
                </label>
              </div>
              
              <!-- Custom Item Form -->
              <custom-item-form @custom-item-added="handleAddCustomItem" />
            </div>
          </div>

          <!-- Right Column -->
          <div class="lg:col-span-2 space-y-6">
            <!-- Estimation Table Section -->
            <div class="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-6">
              <item-table
                :items="selectedItems"
                :creative-supervision-hours="creativeSupervisionHours"
                :total-items="selectedItems.length"
                :total-hours="totalHours"
                :base-hours="baseHours"
                :supervision-only-hours="supervisionOnlyHours"
                :total-supervision-hours="totalSupervisionHours"
                @item-updated="updateTotalHours"
                @item-hours-updated="updateItemHours"
                @item-removed="removeItem"
                @toggle-supervision-only="toggleSupervisionOnly"
              />
            </div>

            <!-- Notes Section -->
            <div class="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-6">
              <notes-section v-model:notes="notes" />
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Replace footer with component -->
    <footer-section />
  </div>
</template>

<script>
import { ref, watchEffect } from "vue";
import { useToast } from "vue-toastification";
import { useBudgetCalculator } from "../composables/useBudgetCalculator.js";
import BaseInfoForm from "../components/forms/BaseInfoForm.vue";
import ServiceSelector from "../components/forms/ServiceSelector.vue";
import CustomItemForm from "../components/forms/CustomItemForm.vue";
import ItemTable from "../components/shared/ItemTable.vue";
import NotesSection from "../components/shared/NotesSection.vue";
import FooterSection from '../components/ui/Footer.vue'
import { useRoute } from 'vue-router';
import { calculateTotalHours } from "../utils/budgetCalculations.js";
import { useAuth } from '@/composables/useAuth'; // Import useAuth

export default {
  name: 'BudgetCalculatorPortinos',
  components: {
    BaseInfoForm,
    ServiceSelector,
    CustomItemForm,
    ItemTable,
    NotesSection,
    FooterSection,
  },
  setup() {
    const toast = useToast();
    const { getAuthHeader } = useAuth(); // Get getAuthHeader
    const {
      clientName,
      ownerName,
      jiraTicket,
      selectedService,
      selectedItems,
      baseHours,
      supervisionOnlyHours,
      totalSupervisionHours,
      totalHours,
      creativeSupervisionHours,
      addItem,
      addCustomItem,
      updateTotalHours,
      updateItemHours, // Ensure updateItemHours is destructured
      toggleSupervisionOnly,
      removeItem,
    } = useBudgetCalculator();

    const editingBudgetId = ref(null);
    const notes = ref("");
    const route = useRoute();
    const selectedTeam = ref('');
    const intranetUrl = ref('');
    const isFormValid = ref(false);
    const supervisionOnlyService = ref(false);

    const updateFormValidation = (isValid) => {
      isFormValid.value = isValid;
    };

    const loadBudget = async (id) => {
      try {
        const headers = getAuthHeader(); // Get auth headers
        const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${apiUrl}/api/budgets/${id}`, {
          headers // Add headers to fetch options
        });
        if (!response.ok) {
          if (response.status === 401) {
            toast.error("No autorizado para cargar esta estimación. Por favor, inicie sesión.");
            // Optionally, redirect to login or handle appropriately
            // router.push('/login'); 
            throw new Error('Unauthorized');
          }
          throw new Error('Network response was not ok');
        }
        
        const budget = await response.json();
        
        // Reset current state
        selectedService.value = null;
        
        // Update all form fields with budget data
        clientName.value = budget.clientName || '';
        ownerName.value = budget.ownerName || '';
        jiraTicket.value = budget.jiraTicket || '';
        intranetUrl.value = budget.intranetUrl || '';
        
        // Update all form fields with budget data
        clientName.value = budget.clientName || '';
        ownerName.value = budget.ownerName || '';
        jiraTicket.value = budget.jiraTicket || '';
        
        // Parse items and filter out supervision entry if it exists
        let parsedItems;
        if (typeof budget.items === 'string') {
          // Items are still stored as JSON string (legacy)
          parsedItems = JSON.parse(budget.items);
        } else if (Array.isArray(budget.items)) {
          // Items are already parsed as array (from updated backend)
          parsedItems = budget.items;
        } else {
          // Fallback for unexpected format
          parsedItems = [];
        }
        
        selectedItems.value = parsedItems.filter(item => 
          (item.service?.name || item.name) !== "Supervisión Creativa"
        ).map(item => {
          // Handle both new and legacy item structures
          let serviceName, hours, defaultHours;
          
          if (item.service) {
            // New structure: item has service object
            serviceName = item.service.name;
            hours = parseFloat(item.service.hours) || parseFloat(item.service.defaultHours) || 0;
            defaultHours = parseFloat(item.service.defaultHours);
          } else {
            // Legacy/database structure: item has direct properties
            serviceName = item.name;
            hours = parseFloat(item.hours) || 0;
            defaultHours = hours; // Use hours as default for legacy items
          }
          
          const quantity = parseFloat(item.quantity) || 1;
          
          return {
            service: {
              name: serviceName,
              hours: Math.round(hours * 10) / 10,
              defaultHours: defaultHours
            },
            quantity: quantity,
            totalHours: Math.round((hours * quantity) * 10) / 10,
            supervisionOnly: item.supervisionOnly || false // Preserve supervision-only flag
          };
        });
        
        notes.value = budget.notes || "";
        editingBudgetId.value = id;
        intranetUrl.value = budget.intranetUrl || '';
      } catch (error) {
        console.error('Error loading budget:', error);
        // Avoid showing generic toast if specific unauthorized message was shown
        if (error.message !== 'Unauthorized') {
          toast.error("Error al cargar la estimación: " + error.message);
        }
      }
    };

    // Watch for route changes to load budget
    watchEffect(async () => {
      const budgetId = route.query.budgetId;
      // Only load if we have a budgetId and it's different from currently editing budget
      if (budgetId && budgetId !== editingBudgetId.value?.toString()) {
        await loadBudget(budgetId);
      }
    });

    const selectService = (service, teamInfo) => {
      selectedService.value = service;
      // Store the team information separately
      if (teamInfo) {
        selectedTeam.value = teamInfo;
      }
    };

    const updateClientName = (name) => {
      clientName.value = name;
    };

    const updateOwnerName = (name) => {
      ownerName.value = name;
    };

    const updateJiraTicket = (ticket) => {
      jiraTicket.value = ticket || '';
    };

    const updateIntranetUrl = (url) => {
      intranetUrl.value = url || '';
    };

    // Wrapper methods to handle supervision-only parameters
    const handleAddItem = () => {
      // Temporarily add team info to service for addItem
      if (selectedService.value && selectedTeam.value) {
        selectedService.value.selectedTeam = selectedTeam.value;
      }
      
      const result = addItem(supervisionOnlyService.value);
      
      // Clean up the temporary team info to keep the original service object intact
      if (selectedService.value && selectedService.value.selectedTeam) {
        delete selectedService.value.selectedTeam;
      }
      
      if (result) {
        if (result.success) {
          toast.success(result.message);
        } else {
          toast.warning(result.message);
        }
      }
      supervisionOnlyService.value = false; // Reset after adding
    };

    const handleAddCustomItem = (customItem, supervisionOnly = false) => {
      addCustomItem(customItem, supervisionOnly);
    };

    const saveBudget = async () => {
      const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3001';
      const url = editingBudgetId.value
        ? `${apiUrl}/api/budgets/${editingBudgetId.value}`
        : `${apiUrl}/api/budgets`;
      const method = editingBudgetId.value ? "PUT" : "POST";

      const budgetData = getTotalWithSupervision();
      const headers = { 
        "Content-Type": "application/json",
        ...getAuthHeader() // Add auth header
      };

      try {
        const response = await fetch(url, {
          method,
          headers, // Use updated headers
          body: JSON.stringify({
            type: 'regular',
            clientName: clientName.value,
            ownerName: ownerName.value,
            items: budgetData.items,
            supervisionHours: budgetData.supervisionHours,
            totalHours: budgetData.totalHours,
            notes: notes.value,
            jiraTicket: jiraTicket.value || '',
            intranetUrl: intranetUrl.value || ''
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('API Error Response:', errorData);
          
          // Handle specific validation errors
          if (response.status === 400 && errorData.details) {
            const validationErrors = errorData.details.map(err => 
              `${err.field}: ${err.message}`
            ).join('\n');
            throw new Error(`Validation failed:\n${validationErrors}`);
          }
          
          throw new Error(`API Error: ${errorData.error || response.statusText}`);
        }

        const data = await response.json();
        
        // Show success message
        toast.success(
          editingBudgetId.value 
            ? "¡Estimación actualizada exitosamente!"
            : `¡Estimación guardada exitosamente! ID: ${data.id}`
        );

        // Update the ID for both new budgets and existing ones (to ensure consistency)
        if (data.id) {
          editingBudgetId.value = data.id;
          // Update URL to reflect the budget ID without triggering navigation
          if (route.query.budgetId !== data.id.toString()) {
            window.history.replaceState(null, '', `?budgetId=${data.id}`);
          }
        }
      } catch (error) {
        console.error('Error saving budget:', error);
        
        // Show detailed error message if available
        if (error.message.includes('Validation failed:')) {
          toast.error(`Error de validación:\n${error.message.replace('Validation failed:\n', '')}`);
        } else {
          toast.error("Error al guardar la estimación: " + error.message);
        }
      }
    };

    const getTotalWithSupervision = () => {
      // Calculate supervision hours using consistent utility
      const supervisionHours = selectedItems.value.length > 0 ? creativeSupervisionHours.value : 0;

      // DON'T add artificial supervision item to the saved items array
      // Just use the original selectedItems for saving
      const itemsToSave = [...selectedItems.value];

      // Calculate final total using utility function for consistency
      const finalTotal = calculateTotalHours(itemsToSave);

      return {
        items: itemsToSave, // Save only the actual user-selected items
        supervisionHours: supervisionHours,
        totalHours: finalTotal
      };
    };

    return {
      clientName,
      ownerName,
      jiraTicket,
      updateClientName,
      updateOwnerName,
      updateJiraTicket,
      selectedService,
      selectedItems,
      totalHours,
      creativeSupervisionHours,
      addItem,
      addCustomItem,
      updateTotalHours,
      updateItemHours, // Ensure updateItemHours is returned
      toggleSupervisionOnly,
      removeItem,
      selectService,
      saveBudget,
      loadBudget,
      editingBudgetId,
      notes,
      getTotalWithSupervision,
      intranetUrl,
      updateIntranetUrl,
      baseHours, // Ensure baseHours is returned
      supervisionOnlyHours,
      totalSupervisionHours,
      supervisionOnlyService,
      handleAddItem,
      handleAddCustomItem,
      isFormValid,
      updateFormValidation,
    };
  },
};
</script>